import { create } from 'zustand';
import { useShallow } from 'zustand/shallow';

type NavbarPanel = 'notifications' | 'team' | 'leaderboard' | 'celebrations' | 'rewards';

interface NavbarPanelsState {
	openPanel: NavbarPanel | null;
	openNotifications: () => void;
	openTeam: () => void;
	openLeaderboard: () => void;
	openCelebrations: () => void;
	openRewards: () => void;
	toggleNotifications: () => void;
	toggleTeam: () => void;
	toggleLeaderboard: () => void;
	toggleCelebrations: () => void;
	toggleRewards: () => void;
	closeAll: () => void;
}

export const useNavbarPanelsStore = create<NavbarPanelsState>((set, get) => ({
	openPanel: null,

	openNotifications: () => set({ openPanel: 'notifications' }),
	openTeam: () => set({ openPanel: 'team' }),
	openLeaderboard: () => set({ openPanel: 'leaderboard' }),
	openCelebrations: () => set({ openPanel: 'celebrations' }),
	openRewards: () => set({ openPanel: 'rewards' }),

	toggleNotifications: () => set({ openPanel: get().openPanel === 'notifications' ? null : 'notifications' }),
	toggleTeam: () => set({ openPanel: get().openPanel === 'team' ? null : 'team' }),
	toggleLeaderboard: () => set({ openPanel: get().openPanel === 'leaderboard' ? null : 'leaderboard' }),
	toggleCelebrations: () => set({ openPanel: get().openPanel === 'celebrations' ? null : 'celebrations' }),
	toggleRewards: () => set({ openPanel: get().openPanel === 'rewards' ? null : 'rewards' }),

	closeAll: () => set({ openPanel: null }),
}));

// Convenience hooks for specific panels
export const useNavbarPanels = () => useNavbarPanelsStore((state) => state);

export const useNotificationsPanel = () =>
	useNavbarPanelsStore(
		useShallow((state) => ({
			isOpen: state.openPanel === 'notifications',
			open: state.openNotifications,
			toggle: state.toggleNotifications,
			close: state.closeAll,
		}))
	);

export const useTeamPanel = () =>
	useNavbarPanelsStore(
		useShallow((state) => ({
			isOpen: state.openPanel === 'team',
			open: state.openTeam,
			toggle: state.toggleTeam,
			close: state.closeAll,
		}))
	);

export const useLeaderboardPanel = () =>
	useNavbarPanelsStore(
		useShallow((state) => ({
			isOpen: state.openPanel === 'leaderboard',
			open: state.openLeaderboard,
			toggle: state.toggleLeaderboard,
			close: state.closeAll,
		}))
	);

export const useCelebrationsPanel = () =>
	useNavbarPanelsStore(
		useShallow((state) => ({
			isOpen: state.openPanel === 'celebrations',
			open: state.openCelebrations,
			toggle: state.toggleCelebrations,
			close: state.closeAll,
		}))
	);

export const useRewardsPanel = () =>
	useNavbarPanelsStore(
		useShallow((state) => ({
			isOpen: state.openPanel === 'rewards',
			open: state.openRewards,
			toggle: state.toggleRewards,
			close: state.closeAll,
		}))
	);

// For closing all panels from nav items
export const useCloseNavbarPanels = () => useNavbarPanelsStore((state) => state.closeAll);

// True when any panel is open (used to suppress route-based active state)
export const useIsAnyPanelOpen = () => useNavbarPanelsStore((state) => state.openPanel !== null);
